import {
  Colors,
  ComboboxPlacements,
  FontIcon,
  FontIcons,
  ListboxOption,
  Placements,
  StandardTagger,
  StandardTooltip,
  useDebounce
} from '@brandfolder/react';
import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';

import { useFetch } from '@api/ApiHelper';
import { ApiDataResponse } from '@api/v4/ApiResponseTypes';
import { User } from '@api/v4/UserTypes';
import { getStandardTaggerLabels } from '@translations';

export interface EmailAddressesProps {
  setFieldTouched: (field: string, isTouched: boolean) => void
  setFieldValue: (field: string, value: string[]) => void;
  clearTags?: boolean;
  /** Used for `@testing-library/react` tests only */
  delay?: number;
  error?: string;
  touched?: boolean;
}

export const EmailAddresses: FunctionComponent<EmailAddressesProps> = (props) => {
  const { clearTags, delay = 400, error, setFieldTouched, setFieldValue, touched } = props;

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<ListboxOption[]>([]);
  const [query, setQuery] = useState('');
  const queryDebounced = useDebounce(query, delay);

  const { error: fetchError, fetch, loading: fetchLoading, response } = useFetch<ApiDataResponse<User, 'users'>>({
    body: {
      order: 'ASC',
      per: 10,
      query: `${queryDebounced}*`,
      sort_by: 'email' // eslint-disable-line @typescript-eslint/naming-convention
    },
    fetchOnMount: false,
    method: 'POST',
    url: `/api/v4/private/${BFG.resource.type}s/${BFG.resource.key}/organization/users/search`
  });

  const resetEmailAddresses = (): void => {
    setFieldTouched('emails', false);
    setQuery('');
    setLoading(false);
  };

  useEffect(() => {
    if (touched) {
      fetch();
    }
  }, [queryDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fetchError) {
      setLoading(false);
    }
  }, [fetchError]);

  useEffect(() => {
    if (response) {
      setOptions(
        response.data.map((user) => {
          const { attributes, id } = user;
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { email, first_name, last_name } = attributes;

          const name = first_name && last_name ? `${first_name} ${last_name}` : '';

          return {
            children: (
              <span className='invitation-form__user-option'>
                <span className='invitation-form__name'>{name || email}</span>
                {email && name && (
                  <small className='invitation-form__email'>{email}</small>
                )}
              </span>
            ),
            label: name || email,
            key: id,
            tooltip: name ? email : undefined,
            value: email
          };
        })
      );
      setLoading(false);
    }
  }, [response]);

  useEffect(() => {
    if (clearTags) {
      resetEmailAddresses();
    }
  }, [clearTags]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StandardTagger
      className='invitation-form__emails'
      clearTags={clearTags}
      comboboxProps={{
        comboboxTextareaProps: {
          onChange: (e): void => {
            setLoading(true);
            setFieldTouched('emails', true);
            setQuery(e.target.value);
          },
          placeholder: bfTranslate('Search users or enter email addresses')
        },
        placement: ComboboxPlacements.BottomStart,
        possiblePlacements: [ComboboxPlacements.BottomStart]
      }}
      error={error}
      height={142}
      id="invite-users-form__emails"
      labels={getStandardTaggerLabels({
        emptyLabel: bfTranslate('No users'),
        iconButtonLabel: bfTranslate('Toggle users'),
        label: bfTranslate('Email addresses'),
        listboxLabel: bfTranslate('List of users'),
        removeTagLabel: bfTranslate('Remove user')
      })}
      loading={fetchLoading || loading}
      onTagged={(tags): void => {
        setFieldValue('emails', [...tags.map((t) => t.value.toString())]);
        resetEmailAddresses();
      }}
      options={options}
      search={false}
      tagFormatter={(tag): ReactNode => {
        const userTag = (
          <span className='invitation-form__user-tag'>
            <FontIcon icon={tag.type === 'create' ? FontIcons.UserInvited : FontIcons.Person} iconSize={13} />
            <span>{tag.label}</span>
          </span>
        );
        if (!tag.tooltip) return userTag;
        return (
          <StandardTooltip
            color={Colors.Primary}
            id={`${tag.key}-tooltip`}
            placement={Placements.TopCenter}
            tooltip={tag.tooltip || ''}
            triggerOffset={15}
          >
            {userTag}
          </StandardTooltip>
        );
      }}
    />
  );
};
